// src/views/adminView/contractsView/compliance/useComplianceStore.js
// ─────────────────────────────────────────────────────────────────────────────
// Persistencia FRONT-ONLY del cumplimiento documental en localStorage (el
// backend no lo persiste — mismo patrón que prospects/useProspectDocs.js:
// errores de parse/quota tragados en silencio).
//
// Key:
//   bt.empresa.compliance.<contractId> → { docs: { [docTipoId]: {estado, venceAt?, solicitadoAt?} } }
//
// Los usuarios de empresa dejaron de vivir acá: la lista real viene de
// useClientAccounts (users/clients) y se administra en el tab Cuentas. Las
// keys bt.empresa.usuarios.<contractId> que hayan quedado en navegadores
// viejos simplemente no se leen más.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { PLANTILLA_REGLAS, venceSegunRegla } from './complianceUtils';

const DOCS_PREFIX = 'bt.empresa.compliance.';

const docsKey = (contractId) => `${DOCS_PREFIX}${contractId}`;

// ── Versión del store ────────────────────────────────────────────────────────
// Contador módulo-level que se incrementa en CADA write del store (docs y
// usuarios). Los componentes que derivan datos leyendo localStorage en
// sincrónico (badges de tabs, Stat de cartera, tablas) se suscriben vía
// useComplianceVersion() y lo agregan a sus deps: cualquier mutación hecha en
// otro componente (drawer, expediente del detalle) los re-renderiza en vivo.
let complianceVersion = 0;
const versionListeners = new Set();

function bumpComplianceVersion() {
  complianceVersion += 1;
  versionListeners.forEach((listener) => listener());
}

function subscribeComplianceVersion(listener) {
  versionListeners.add(listener);
  return () => versionListeners.delete(listener);
}

const getComplianceVersion = () => complianceVersion;

/**
 * Hook: número que cambia con cada write del store de cumplimiento. Usarlo en
 * deps de useMemo/cálculos que leen localStorage con readComplianceStored
 * para que recalculen en vivo tras cualquier mutación.
 */
export function useComplianceVersion() {
  return useSyncExternalStore(subscribeComplianceVersion, getComplianceVersion);
}

/**
 * Lectura síncrona NO-hook del estado documental guardado. La usan
 * CumplimientoChip y los cálculos de cartera en listas (rules of hooks).
 */
export function readComplianceStored(contractId) {
  if (!contractId) return null;
  try {
    const raw = window.localStorage.getItem(docsKey(contractId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (err) {
    return null; // parse/security errors → como si no hubiera nada guardado
  }
}

function writeComplianceStored(contractId, stored) {
  if (!contractId) return;
  try {
    window.localStorage.setItem(docsKey(contractId), JSON.stringify(stored));
  } catch (err) {
    /* quota/security: se ignora en silencio (front-only) */
  }
  bumpComplianceVersion();
}

/** Hook reactivo sobre el estado documental de un contract. */
export function useComplianceDocs(contractId) {
  const [stored, setStored] = useState(() => readComplianceStored(contractId));

  useEffect(() => {
    setStored(readComplianceStored(contractId));
  }, [contractId]);

  const mutateDoc = useCallback(
    (docTipoId, patch) => {
      if (!contractId || !docTipoId) return;
      const current = readComplianceStored(contractId) ?? { docs: {} };
      const docs = { ...(current.docs || {}) };
      docs[docTipoId] = { ...(docs[docTipoId] || {}), ...patch };
      writeComplianceStored(contractId, { ...current, docs });
      // Re-lee para reactividad local (y para reflejar lo que realmente quedó).
      setStored(readComplianceStored(contractId));
    },
    [contractId],
  );

  const setDocEstado = useCallback(
    (docTipoId, estado) => {
      const patch = { estado };
      if (estado === 'vigente') {
        // Al aprobar se recalcula el vencimiento DESDE HOY según la regla de la
        // plantilla — no se arrastran fechas viejas. Si la regla no vence,
        // venceAt queda undefined (JSON lo omite).
        const regla = PLANTILLA_REGLAS.find((r) => r.docTipoId === docTipoId);
        patch.venceAt = regla ? venceSegunRegla(regla) : undefined;
      }
      mutateDoc(docTipoId, patch);
    },
    [mutateDoc],
  );

  const marcarSolicitado = useCallback(
    (docTipoId) => {
      mutateDoc(docTipoId, { solicitadoAt: new Date().toISOString() });
    },
    [mutateDoc],
  );

  return { stored, setDocEstado, marcarSolicitado };
}
